/**
 * talepGecmisi.js — what the request history shows and how a value reads (backlog #453, 2026-10-17; backend #452).
 *
 * The backend writes one history row per CHANGED FIELD of a stand request (stand_talep_request / stand_teknik_talep) and answers
 * `GET /stands/talepler/{standId}/gecmis` with events: one save = one event, with the side that made it (FIRMA / ORGANIZATOR / PERSONEL), the company, the user (a name
 * only where the viewer may see it), the time and the changes `{ alan, eski, yeni }`. The values are raw text: a status as "0".."3", a flag as "1" / "0", a date as
 * ISO, a quantity as a plain number. This file turns them into text for the screen; everything here is pure (no React, no calls).
 */

/** The kinds the history can be narrowed to (the backend's `tur`). */
export const GECMIS_TURLERI = { HEPSI: "", TEKNIK: "TEKNIK", TALEP: "REQUEST" };

/**
 * Badge colour per event kind (the backend's `islem`). #696 (backend #695): SUBMIT = a company's Teknik draft sent for approval (Taslak -> Bekliyor),
 * the same colour as "Pending" because that is what the request is from then on.
 */
export const ISLEM_RENK = { CREATE: "success", UPDATE: "info", REVIEW: "primary", WITHDRAW: "secondary", SUBMIT: "warning" };

/** Badge colour per side. */
export const TARAF_RENK = { PERSONEL: "dark", ORGANIZATOR: "warning", FIRMA: "info" };

/** The fields the backend tracks, in the order a screen lists them (the text of each is `portal:talepGecmisi.alan.<alan>`). */
export const GECMIS_ALANLARI = [
  "status", "has_request", "quantity_value", "start_date", "end_date", "sergilenecek_urun", "yapilacak_is", "operator",
  "ekipman_malzeme_listesi", "is_ozeti", "riskler", "kvkk_accepted", "metin_surum_id", "reject_reason",
];

/** A long text is cut on the screen (the whole text stays in the tooltip). */
export const METIN_EN_FAZLA = 160;

const TARIH_ALANLARI = new Set(["start_date", "end_date"]);
const EVET_HAYIR_ALANLARI = new Set(["has_request", "kvkk_accepted"]);

function bos(deger) {
  return deger === null || deger === undefined || String(deger).trim() === "";
}

function gunBicimi(iso, yerel) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso));
  if (!m) return String(iso);
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleDateString(yerel, { day: "2-digit", month: "short", year: "numeric" });
}

/**
 * One value as text. `t` is the i18n function of the `portal` namespace, `yerel` the locale tag (for dates).
 * Nothing → the empty mark; a status → its name; a flag → yes / no; a date → a short date; the kW → "12.5 kW"; text as typed.
 * Returns `{ metin, tam }`: `metin` is what is drawn (long text cut), `tam` the whole text for the tooltip (null when nothing was cut).
 */
export function degerBicimi(alan, deger, t, yerel) {
  if (bos(deger)) return { metin: t("talepGecmisi.bosDeger"), tam: null };
  const ham = String(deger).trim();
  if (alan === "status") {
    const ad = t(`talepGecmisi.durum.${ham}`);
    return { metin: ad === `talepGecmisi.durum.${ham}` ? ham : ad, tam: null };
  }
  if (EVET_HAYIR_ALANLARI.has(alan)) {
    return { metin: ham === "1" || ham.toLowerCase() === "true" ? t("talepGecmisi.evet") : t("talepGecmisi.hayir"), tam: null };
  }
  if (TARIH_ALANLARI.has(alan)) return { metin: gunBicimi(ham, yerel), tam: null };
  if (alan === "quantity_value") return { metin: t("talepGecmisi.kw", { deger: ham }), tam: null };
  if (ham.length > METIN_EN_FAZLA) return { metin: `${ham.slice(0, METIN_EN_FAZLA)}…`, tam: ham };
  return { metin: ham, tam: null };
}

/** The label of a field; an unknown one (a field a later backend adds) shows its own name instead of a missing-key marker. */
export function alanAdi(alan, t) {
  const anahtar = `talepGecmisi.alan.${alan}`;
  const ad = t(anahtar);
  return ad === anahtar ? alan : ad;
}

/** The category an event is about: a Teknik one by its fixed name, a Hizmet / İş İzni / Ekipman one by the job type's name the backend sends. */
export function kategoriAdi(olay, t) {
  if (olay.talepTuru === "TEKNIK") {
    const anahtar = `talepGecmisi.teknik.${olay.kategori}`;
    const ad = olay.kategori ? t(anahtar) : null;
    return ad && ad !== anahtar ? ad : (olay.kategori || `#${olay.kategoriId}`);
  }
  return olay.kategoriAdi || olay.kategori || `#${olay.kategoriId}`;
}

/**
 * Who made the change, for the line of an event: the user's name where the backend sent it, otherwise only the side (SAKA, the organizer, or the company's name);
 * `taraf` is always there, `firma` is the company's name for the sides that have one.
 */
export function yapanBilgisi(olay, t) {
  const taraf = t(`talepGecmisi.taraf.${olay.taraf}`);
  const tarafAdi = taraf === `talepGecmisi.taraf.${olay.taraf}` ? String(olay.taraf ?? "") : taraf;
  return {
    ad: olay.kullaniciAdi || null,
    taraf: tarafAdi,
    firma: olay.taraf === "PERSONEL" ? null : (olay.firmaAdi || null),
  };
}

/** The time of an event, short and in the viewer's language; the raw text when it cannot be read. */
export function olayZamani(tarih, yerel) {
  if (tarih === null || tarih === undefined || tarih === "") return "";
  const d = new Date(tarih);
  return Number.isNaN(d.getTime()) ? String(tarih) : d.toLocaleString(yerel, { dateStyle: "short", timeStyle: "short" });
}

/**
 * The changes of one event in the order of GECMIS_ALANLARI (an unknown field last), so the same field is always in the same place from one event to the next.
 */
export function siraliDegisiklikler(degisiklikler) {
  const sira = (a) => {
    const i = GECMIS_ALANLARI.indexOf(a);
    return i < 0 ? GECMIS_ALANLARI.length : i;
  };
  return [...(degisiklikler ?? [])].sort((x, y) => sira(x.alan) - sira(y.alan));
}
